import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  IoSparklesOutline,
  IoStorefrontOutline,
  IoCarOutline,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoArchiveOutline,
  IoTrendingUpOutline,
  IoTrendingDownOutline,
  IoRemoveOutline,
  IoRefreshOutline,
} from 'react-icons/io5';
import type { ReactNode } from 'react';
import { api } from '../api';
import { Button } from '../design';

// Маппинг сырых статусов Ozon в смысловые группы для склада.
type StatusGroup = 'pickup' | 'in_transit' | 'received' | 'cancelled' | 'other';

const STATUS_GROUP: Record<string, StatusGroup> = {
  ReadyForDelivery:        'pickup',     // готов к выдаче — забрать в ПВЗ
  WaitingForSeller:        'pickup',
  MovingToSeller:          'in_transit', // в пути на склад
  ReceivedBySeller:        'received',   // получен на складе
  Cancelled:               'cancelled',
  CancelledDisputeNotOpen: 'cancelled',
  ReturnedToOzon:          'other',
  CompensationRejected:    'other',
  WriteOff:                'other',
};

const STATUS_LABEL: Record<string, string> = {
  ReadyForDelivery:        'Готов к выдаче в ПВЗ',
  WaitingForSeller:        'Ожидает продавца',
  MovingToSeller:          'В пути на склад',
  ReceivedBySeller:        'Получен на складе',
  Cancelled:               'Отменён',
  CancelledDisputeNotOpen: 'Отменён (без спора)',
  ReturnedToOzon:          'Возвращён в Ozon',
  CompensationRejected:    'Компенсация отклонена',
  WriteOff:                'Списан',
};

const GROUP_META: Record<StatusGroup, { label: string; hint: string; icon: ReactNode; color: 'yellow' | 'blue' | 'green' | 'gray'; statuses: string[] }> = {
  pickup: {
    label: 'Забрать из ПВЗ Ozon',
    hint: 'Готовы к выдаче — нужно забрать',
    icon: <IoStorefrontOutline />,
    color: 'yellow',
    statuses: ['ReadyForDelivery', 'WaitingForSeller'],
  },
  in_transit: {
    label: 'В пути на склад',
    hint: 'Ozon везёт на наш склад',
    icon: <IoCarOutline />,
    color: 'blue',
    statuses: ['MovingToSeller'],
  },
  received: {
    label: 'Принято на складе',
    hint: 'Оприходовать в 1С',
    icon: <IoCheckmarkCircleOutline />,
    color: 'green',
    statuses: ['ReceivedBySeller'],
  },
  cancelled: {
    label: 'Отменены',
    hint: 'Клиент отменил возврат',
    icon: <IoCloseCircleOutline />,
    color: 'gray',
    statuses: ['Cancelled', 'CancelledDisputeNotOpen'],
  },
  other: {
    label: 'Прочее',
    hint: 'Возвращены в Ozon / списаны',
    icon: <IoArchiveOutline />,
    color: 'gray',
    statuses: ['ReturnedToOzon', 'CompensationRejected', 'WriteOff'],
  },
};

const GROUP_ORDER: StatusGroup[] = ['pickup', 'in_transit', 'received', 'cancelled', 'other'];

function statusBadgeColor(status: string): string {
  const group = STATUS_GROUP[status] ?? 'other';
  switch (group) {
    case 'pickup':     return 'badge-warn';
    case 'in_transit': return 'badge-info';
    case 'received':   return 'badge-success';
    default:           return 'badge';
  }
}

export function ReturnsPage() {
  const [params, setParams] = useSearchParams();
  const statusFilter = params.get('status') ?? '';
  const groupFilter = (params.get('group') as StatusGroup | null) ?? null;
  const categoryFilter = params.get('category') ?? '';
  const [showClusters, setShowClusters] = useState(false);
  const queryClient = useQueryClient();

  // Для счётчиков по статусам — отдельный запрос без фильтров.
  const overview = useQuery({
    queryKey: ['returns-overview'],
    queryFn: () => api.returnsList({ limit: 1 }),
  });

  const clusters = useQuery({
    queryKey: ['returns-clusters'],
    queryFn: api.returnsClusters,
  });

  const recompute = useMutation({
    mutationFn: api.returnsClustersRecompute,
    onSuccess: () => {
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['returns-clusters'] }), 30_000);
    },
  });

  // Для списка — с применением фильтра по группе/категории.
  // Если выбрана группа — отправляем один статус из неё; для нескольких статусов фильтруем на клиенте.
  const list = useQuery({
    queryKey: ['returns', statusFilter, groupFilter, categoryFilter],
    queryFn: () => api.returnsList({
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
      limit: 200,
    }),
  });

  const filteredItems = useMemo(() => {
    if (!list.data) return [];
    if (!groupFilter || statusFilter) return list.data.items;
    const allowed = new Set(GROUP_META[groupFilter].statuses);
    return list.data.items.filter((r) => allowed.has(r.status));
  }, [list.data, groupFilter, statusFilter]);

  function setFilter(next: Partial<{ status: string; group: StatusGroup | null; category: string }>) {
    const sp = new URLSearchParams(params);
    if ('status' in next) {
      if (next.status) sp.set('status', next.status);
      else sp.delete('status');
    }
    if ('group' in next) {
      if (next.group) sp.set('group', next.group);
      else sp.delete('group');
    }
    if ('category' in next) {
      if (next.category) sp.set('category', next.category);
      else sp.delete('category');
    }
    setParams(sp, { replace: true });
  }

  // Считаем количество по группам из byStatus.
  const groupCounts = useMemo(() => {
    const counts: Record<StatusGroup, number> = { pickup: 0, in_transit: 0, received: 0, cancelled: 0, other: 0 };
    if (!overview.data?.byStatus) return counts;
    for (const { status, count } of overview.data.byStatus) {
      const g = STATUS_GROUP[status] ?? 'other';
      counts[g] += count;
    }
    return counts;
  }, [overview.data]);

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Возвраты</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            <IoSparklesOutline style={{ verticalAlign: 'middle' }} /> Статусы из Ozon · автокатегоризация причин через локальный AI
          </div>
        </div>
      </div>

      {/* Плитки по статусам — основной фильтр для склада */}
      <div className="returns-status-grid">
        {GROUP_ORDER.map((g) => {
          const meta = GROUP_META[g];
          const count = groupCounts[g];
          const active = groupFilter === g && !statusFilter;
          return (
            <button
              key={g}
              type="button"
              className={`return-status-tile color-${meta.color}${active ? ' active' : ''}`}
              onClick={() => setFilter({ group: active ? null : g, status: '' })}
            >
              <div className="rst-icon">{meta.icon}</div>
              <div className="rst-body">
                <div className="rst-count">{count}</div>
                <div className="rst-label">{meta.label}</div>
                <div className="rst-hint">{meta.hint}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Активный фильтр + сброс */}
      {(groupFilter || statusFilter || categoryFilter) && (
        <div className="muted" style={{ margin: '4px 0 12px', fontSize: 13 }}>
          Фильтр:{' '}
          {groupFilter && <strong style={{ color: 'var(--text)' }}>{GROUP_META[groupFilter].label}</strong>}
          {statusFilter && <strong style={{ color: 'var(--text)' }}>{STATUS_LABEL[statusFilter] ?? statusFilter}</strong>}
          {categoryFilter && (
            <>
              {(groupFilter || statusFilter) && ' · '}
              категория <strong style={{ color: 'var(--text)' }}>{categoryFilter}</strong>
            </>
          )}
          {' · '}
          <button
            className="link-btn"
            onClick={() => setFilter({ group: null, status: '', category: '' })}
          >
            сбросить
          </button>
        </div>
      )}

      {list.isLoading && <div className="card">Загрузка…</div>}
      {list.error && <div className="card" style={{ color: 'var(--red)' }}>{(list.error as Error).message}</div>}

      {/* AI-кластеры свободных текстов причин */}
      {clusters.data && clusters.data.items.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 14 }}>
                <IoSparklesOutline style={{ verticalAlign: 'middle' }} /> AI-кластеры причин (свободные тексты)
              </h3>
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                Группировка свободных формулировок · обновлено {clusters.data.items[0] ? new Date(clusters.data.items[0].updatedAt).toLocaleString('ru-RU') : '—'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => setShowClusters((v) => !v)}>
                {showClusters ? 'Свернуть' : 'Развернуть'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => recompute.mutate()}
                loading={recompute.isPending}
                title="Перезапустить AI-кластеризацию"
                leftIcon={<IoRefreshOutline />}
              >
                Пересчитать
              </Button>
            </div>
          </div>
          {showClusters && (
            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
              {clusters.data.items.map((c) => (
                <div key={c.id} className="cluster-tile">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{c.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>
                      {c.count}
                      {c.trend === 'up' && <IoTrendingUpOutline style={{ color: 'var(--red)', marginLeft: 4, fontSize: 14 }} />}
                      {c.trend === 'down' && <IoTrendingDownOutline style={{ color: 'var(--green)', marginLeft: 4, fontSize: 14 }} />}
                      {c.trend === 'flat' && <IoRemoveOutline style={{ color: 'var(--text-muted)', marginLeft: 4, fontSize: 14 }} />}
                    </div>
                  </div>
                  {c.description && (
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{c.description}</div>
                  )}
                  {c.samples.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                      Примеры: {c.samples.slice(0, 2).map((s, i) => (
                        <span key={i}>
                          {i > 0 && ' · '}
                          <em>«{s.length > 40 ? `${s.slice(0, 40)}…` : s}»</em>
                        </span>
                      ))}
                    </div>
                  )}
                  {c.keywords.length > 0 && (
                    <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {c.keywords.slice(0, 5).map((kw) => (
                        <span key={kw} className="cluster-kw">{kw}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {clusters.data && clusters.data.items.length === 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="muted" style={{ fontSize: 13 }}>
            <IoSparklesOutline style={{ verticalAlign: 'middle' }} /> AI-кластеры причин ещё не построены.
          </div>
          <Button variant="primary" onClick={() => recompute.mutate()} loading={recompute.isPending}>
            Построить
          </Button>
        </div>
      )}

      {list.data && (
        <>
          {/* Категории — вторичный фильтр по AI-причине */}
          {list.data.byCategory.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ padding: '16px 20px 0' }}>
                <h3 style={{ margin: 0, fontSize: 14 }}>По причинам (AI-категоризация)</h3>
              </div>
              <div style={{ padding: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className={`status-tab${!categoryFilter ? ' active' : ''}`}
                  onClick={() => setFilter({ category: '' })}
                >
                  Все ({list.data.total})
                </button>
                {list.data.byCategory.map((c) => (
                  <button
                    key={c.category}
                    className={`status-tab${categoryFilter === c.category ? ' active' : ''}`}
                    onClick={() => setFilter({ category: c.category })}
                  >
                    {c.label} ({c.count})
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <div style={{ padding: '20px 20px 0' }}>
              <h3 style={{ margin: 0 }}>Возвраты ({filteredItems.length})</h3>
            </div>
            <table className="products" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Статус</th>
                  <th>Отправление</th>
                  <th>Товар</th>
                  <th className="num">Цена</th>
                  <th>Причина (Ozon)</th>
                  <th>AI-категория</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 && <tr><td colSpan={7} className="empty">Возвратов нет.</td></tr>}
                {filteredItems.map((r) => (
                  <tr key={r.id}>
                    <td className="muted" style={{ fontSize: 12 }}>{r.returnedAt ? new Date(r.returnedAt).toLocaleDateString('ru-RU') : '—'}</td>
                    <td>
                      <span className={`badge ${statusBadgeColor(r.status)}`} style={{ fontSize: 11 }}>
                        {STATUS_LABEL[r.status] ?? r.status}
                      </span>
                    </td>
                    <td><code style={{ fontSize: 11 }}>{r.postingNumber ?? '—'}</code></td>
                    <td style={{ fontSize: 12 }}>
                      <div>{r.productName ?? '—'}</div>
                      <div className="muted" style={{ fontSize: 11 }}>SKU {r.sku ?? '—'}</div>
                    </td>
                    <td className="num">{r.price ? `${Number(r.price).toFixed(0)} ₽` : '—'}</td>
                    <td style={{ fontSize: 12 }}>{r.returnReasonName ?? '—'}</td>
                    <td>
                      {r.aiCategoryLabel ? (
                        <span className="badge badge-info" title={r.aiReason ?? ''}>{r.aiCategoryLabel}</span>
                      ) : <span className="muted" style={{ fontSize: 11 }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
